<template>
    <div class="mergeable-input">
        <ds-input-field
            ref="inputField"
            v-model="localValue"
            :class="['merge-field-input', { 'input-focus' : inputFocused }]"
            :data-qa="dataQa"
            :intercept-on-update="interceptInputUpdate"
            :invalid="invalid"
            :label="label"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :required="required"
            :submitted="submitted"
            :type="type"
            autocomplete="nofill"
            @blur="blurInput"
            @focus="focusInput"
        >
            <template #help>
                <div v-if="helpText" class="help-text">
                    {{ helpText }}
                </div>
            </template>

            <template #error>
                <div v-if="errorText" data-qa="error-text">
                    {{ errorText }}
                </div>
            </template>

            <template #trailing>
                <ds-spinner
                    v-if="loading"
                    class="loading-spinner"
                    :class="[{ 'focused-loading-spinner': inputFocused }]"
                    :size="20"
                />

                <merge-field-dropdown
                    v-click-outside="() => isMergeFieldDropdownOpen = false"
                    class="merge-field-dropdown"
                    data-qa="merge-field-dropdown"
                    :merge-field-categories="mergeFieldCategories"
                    :is-open="isMergeFieldDropdownOpen"
                    @menuOpen="handleMenuOpen"
                    @menuClosed="handleMenuClosed"
                    @select="insertTextIntoInput"
                >
                    <template #action>
                        <ds-icon-button
                            tabindex="-1"
                            :aria-label="$t('components.shared.mergeableInput.merge')"
                            class="merge-field-button"
                            :class="[{ 'input-focus': inputFocused }]"
                            @click="isMergeFieldDropdownOpen = !isMergeFieldDropdownOpen"
                            @focus="handleMenuOpen"
                            @mousedown.prevent
                        >
                            <ds-tooltip position="top" class="tooltip-container">
                                <template #reference>
                                    <ds-icon class="merge-field-icon" name="merge-field" />
                                </template>

                                <template #default>
                                    <span>{{ $t('components.shared.mergeFieldDropdown.tooltip') }}</span>
                                </template>
                            </ds-tooltip>
                        </ds-icon-button>
                    </template>
                </merge-field-dropdown>
            </template>
        </ds-input-field>
    </div>
</template>

<script>
import MergeFieldDropdown from '@/components/shared/MergeFieldDropdown';

export default {
    components: {
        MergeFieldDropdown,
    },

    props: {
        dataQa: String,
        errorText: {
            type: String,
            default: null,
        },
        helpText: {
            type: String,
            default: null,
        },
        invalid: Boolean,
        label: {
            type: String,
            default: null,
        },
        loading: Boolean,
        maxlength: Number,
        mergeFieldCategories: {
            type: Array,
            default: () => [],
        },
        placeholder: String,
        required: Boolean,
        submitted: Boolean,
        tracking: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        value: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            inputFocused: false,
            localValue: '',
            menuOpen: false,
            newVal: '',
            updateCallback: Function,
            isMergeFieldDropdownOpen: false,
        };
    },

    created() {
        this.localValue = this.value;
    },

    watch: {
        localValue(val) {
            if (val !== this.value) {
                this.$emit('input', val);
            }
        },

        value(val) {
            this.localValue = val;
        },
    },

    methods: {
        blurInput() {
            this.$emit('blur-input');

            return this.$nextTick().then(() => {
                if (!this.menuOpen) {
                    this.inputFocused = false;
                }
            });
        },

        focusInput() {
            this.inputFocused = true;
            this.$refs.inputField.input_focus();
        },

        getActiveElement() {
            return document.activeElement;
        },

        handleMenuOpen() {
            this.inputFocused = true;
            this.$refs.inputField.input_focus();
            this.menuOpen = true;
        },

        handleMenuClosed() {
            this.menuOpen = false;

            const inputHasFocus = this.$refs.inputField.$refs.input === this.getActiveElement();

            if (!inputHasFocus) {
                this.inputFocused = false;
            }
        },

        insertTextIntoInput(text = { value: '' }) {
            const { input } = this.$refs.inputField.$refs;
            const { selectionStart, selectionEnd } = input;
            const inputValue = this.localValue || '';

            this.localValue = inputValue.substr(0, selectionStart) + text.value + inputValue.substr(selectionEnd);

            if (this.tracking) {
                this.$track(this.tracking);
            }
            input.focus();

            this.isMergeFieldDropdownOpen = false;
        },

        interceptInputUpdate(newVal, updateCallback) {
            this.newVal = newVal;
            this.updateCallback = updateCallback;

            updateCallback(newVal);
            this.focusInput();
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
    $button-height: $input-height - px-to-rem(2);

    .mergeable-input {
        width: 100%;
        display: flex;
        align-items: center;
        margin-bottom: var(--mergable-input-margin-bottom, $spacing-200);
        position: relative;

        .merge-field-input {
            margin-bottom: 0;
            @include border-end(0);
        }
    }

    .help-text {
        color: var(--help-text-color, inherit);
    }

    .merge-field-dropdown {
        margin: -$spacing-100 0;
        @include margin-start($spacing-100);
    }

    .merge-field-button {
        flex-direction: column;
        height: $button-height;
        display: none;
        border: 1px solid transparent;
        @include border-radius(0 #{$input-border-radius} #{$input-border-radius} 0);
        margin-right: px-to-rem(-14);
        padding: px-to-rem(1);
        &.input-focus {
            display: flex;
            @include border-start(1px solid $color-gray-300);
        }
        .merge-field-icon {
            --icon-size: 1rem;
            margin: px-to-rem(11);
        }
    }

    .tooltip-container span {
        font-size: $font-size-md;
    }

    .loading-spinner {
        margin-top: px-to-rem(2);
    }

    .focused-loading-spinner {
        margin-bottom: px-to-rem(10);
    }
</style>
